import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Upload, Trash2, Copy, Check, Download, FileIcon, X, Eye, Globe, Monitor, Clock, User, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '@/App';
import AdminLayout from '@/components/AdminLayout';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const EXPIRATION_OPTIONS = [
  { value: '0', label: 'Süresiz' },
  { value: '1', label: '1 gün sonra' },
  { value: '7', label: '7 gün sonra' },
  { value: '30', label: '30 gün sonra' },
  { value: '90', label: '90 gün sonra' },
  { value: '365', label: '1 yıl sonra' },
];

function formatExpiration(expiresAt) {
  if (!expiresAt) return null;
  const expDate = new Date(expiresAt);
  if (Number.isNaN(expDate.getTime())) return null;
  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();
  if (diffMs <= 0) return { text: 'Süresi doldu', expired: true };
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return {
    text: days === 1 ? 'Yarın silinecek' : `${days} gün sonra silinecek`,
    expired: false,
    date: expDate.toLocaleString('tr-TR'),
  };
}

function parseUserAgent(ua) {
  if (!ua || ua === 'unknown') return { browser: 'Bilinmiyor', os: 'Bilinmiyor', device: 'Bilinmiyor' };

  let browser = 'Bilinmiyor';
  let os = 'Bilinmiyor';
  let device = 'Masaüstü';

  // Browser detection
  if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('OPR/') || ua.includes('Opera')) browser = 'Opera';
  else if (ua.includes('Chrome/') && !ua.includes('Edg/')) browser = 'Chrome';
  else if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('MSIE') || ua.includes('Trident/')) browser = 'Internet Explorer';

  // OS detection
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS X') || ua.includes('Macintosh')) os = 'macOS';
  else if (ua.includes('Linux') && !ua.includes('Android')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  // Device detection
  if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) device = 'Mobil';
  else if (ua.includes('iPad') || ua.includes('Tablet')) device = 'Tablet';

  return { browser, os, device };
}

const AdminFileShare = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const abortControllerRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);
  const [detailFile, setDetailFile] = useState(null);
  const [downloadLogs, setDownloadLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expireDays, setExpireDays] = useState('0');
  const [editingExpire, setEditingExpire] = useState(false);
  const [editExpireDays, setEditExpireDays] = useState('0');
  const [savingExpire, setSavingExpire] = useState(false);

  const fetchFiles = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/shared-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching shared files:', error);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024 * 1024) {
      toast.error('Dosya boyutu 15GB sınırını aşıyor');
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setUploading(true);
    setUploadProgress(0);
    setUploadFileName(file.name);

    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const expireParam = parseInt(expireDays, 10);
      await axios.post(`${API}/admin/shared-files`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        params: expireParam > 0 ? { expire_days: expireParam } : undefined,
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          const percent = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(percent);
        }
      });

      const expireLabel = EXPIRATION_OPTIONS.find((o) => o.value === expireDays)?.label;
      toast.success(
        expireParam > 0 ? `Dosya yüklendi! (${expireLabel} otomatik silinecek)` : 'Dosya yüklendi!'
      );
      fetchFiles();
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        toast.info('Dosya yükleme iptal edildi');
      } else {
        const detail = error.response?.data?.detail || 'Dosya yüklenemedi';
        toast.error(detail);
      }
    } finally {
      abortControllerRef.current = null;
      setUploading(false);
      setUploadProgress(0);
      setUploadFileName('');
      e.target.value = '';
    }
  };

  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`"${fileName}" dosyasını silmek istediğinize emin misiniz?`)) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/shared-files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dosya silindi');
      fetchFiles();
    } catch (error) {
      toast.error('Dosya silinemedi');
    }
  };

  const getShareLink = (shareCode) => {
    const baseUrl = BACKEND_URL || window.location.origin;
    return `${baseUrl}/api/shared/${shareCode}`;
  };

  const handleCopyLink = async (shareCode, fileId) => {
    const link = getShareLink(shareCode);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(fileId);
      toast.success('Bağlantı kopyalandı!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(fileId);
      toast.success('Bağlantı kopyalandı!');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleSaveExpire = async () => {
    if (!detailFile) return;
    setSavingExpire(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.patch(
        `${API}/admin/shared-files/${detailFile.id}`,
        { expire_days: parseInt(editExpireDays, 10) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetailFile((prev) => (prev ? { ...prev, expires_at: response.data.expires_at } : prev));
      setFiles((prev) => prev.map((f) => (f.id === detailFile.id ? { ...f, expires_at: response.data.expires_at } : f)));
      toast.success('Son kullanma süresi güncellendi');
      setEditingExpire(false);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Süre güncellenemedi';
      toast.error(detail);
    } finally {
      setSavingExpire(false);
    }
  };

  const handleShowDetail = async (file) => {
    setDetailFile(file);
    setEditingExpire(false);
    setLogsLoading(true);
    setDownloadLogs([]);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/shared-files/${file.id}/downloads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDownloadLogs(response.data);
    } catch (error) {
      console.error('Error fetching download logs:', error);
      toast.error('İndirme logları yüklenemedi');
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dosya Paylaşımı</h1>
            <p className="text-gray-600 mt-1">Dosya yükleyin, bağlantı ile paylaşın</p>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex flex-col">
              <label htmlFor="expire-days" className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <CalendarClock className="h-3 w-3" />
                Otomatik Silme
              </label>
              <select
                id="expire-days"
                value={expireDays}
                onChange={(e) => setExpireDays(e.target.value)}
                disabled={uploading}
                className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                {EXPIRATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleUpload}
            />
            {!uploading && (
              <Button
                onClick={() => document.getElementById('file-upload').click()}
                size="lg"
              >
                <Upload className="mr-2 h-4 w-4" />
                Dosya Yükle
              </Button>
            )}
          </div>
        </div>

        {uploading && (
          <Card className="mb-4">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Upload className="h-4 w-4 text-blue-600 shrink-0 animate-pulse" />
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {uploadFileName}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold text-blue-600">
                    %{uploadProgress}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelUpload}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </CardContent>
          </Card>
        )}

        {files.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-500">Henüz dosya yok</h3>
              <p className="text-gray-400 mt-1">Dosya yükleyerek paylaşılabilir bağlantılar oluşturun</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {files.map((file) => (
              <Card key={file.id}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="bg-blue-50 p-2.5 rounded-lg shrink-0">
                        <FileIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{file.original_name}</p>
                        <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap">
                          <span>{formatFileSize(file.file_size)}</span>
                          <span className="flex items-center gap-1">
                            <Download className="h-3 w-3" />
                            {file.download_count} indirme
                          </span>
                          <span>
                            {new Date(file.created_at).toLocaleDateString('tr-TR')}
                          </span>
                          {(() => {
                            const exp = formatExpiration(file.expires_at);
                            if (!exp) return null;
                            return (
                              <span
                                title={exp.date || ''}
                                className={`flex items-center gap-1 ${exp.expired ? 'text-red-600' : 'text-amber-600'}`}
                              >
                                <CalendarClock className="h-3 w-3" />
                                {exp.text}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleShowDetail(file)}
                      >
                        <Eye className="mr-1.5 h-4 w-4" />
                        Detay
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyLink(file.share_code, file.id)}
                      >
                        {copiedId === file.id ? (
                          <>
                            <Check className="mr-1.5 h-4 w-4 text-green-600" />
                            Kopyalandı
                          </>
                        ) : (
                          <>
                            <Copy className="mr-1.5 h-4 w-4" />
                            Bağlantı Kopyala
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(file.id, file.original_name)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* File Detail Modal */}
        <Dialog open={!!detailFile} onOpenChange={(open) => { if (!open) setDetailFile(null); }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {detailFile && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileIcon className="h-5 w-5 text-blue-600" />
                    Dosya Detayları
                  </DialogTitle>
                  <DialogDescription>
                    {detailFile.original_name}
                  </DialogDescription>
                </DialogHeader>

                {/* File Info */}
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Dosya Boyutu</p>
                    <p className="font-medium text-gray-900">{formatFileSize(detailFile.file_size)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Toplam İndirme</p>
                    <p className="font-medium text-gray-900">{detailFile.download_count}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Yüklenme Tarihi</p>
                    <p className="font-medium text-gray-900">
                      {new Date(detailFile.created_at).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Yükleyen</p>
                    <p className="font-medium text-gray-900">{detailFile.created_by || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-500">Otomatik Silme</p>
                      {!editingExpire && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditExpireDays('0');
                            setEditingExpire(true);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Düzenle
                        </button>
                      )}
                    </div>
                    {editingExpire ? (
                      <div className="flex items-center gap-2 mt-1">
                        <select
                          value={editExpireDays}
                          onChange={(e) => setEditExpireDays(e.target.value)}
                          disabled={savingExpire}
                          className="h-9 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          {EXPIRATION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.value === '0' ? 'Süresiz yap' : opt.label.replace(' sonra', ' sonra silinsin')}
                            </option>
                          ))}
                        </select>
                        <Button size="sm" onClick={handleSaveExpire} disabled={savingExpire}>
                          {savingExpire ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingExpire(false)}
                          disabled={savingExpire}
                        >
                          İptal
                        </Button>
                      </div>
                    ) : detailFile.expires_at ? (
                      (() => {
                        const exp = formatExpiration(detailFile.expires_at);
                        return (
                          <p className={`font-medium ${exp?.expired ? 'text-red-600' : 'text-gray-900'}`}>
                            {new Date(detailFile.expires_at).toLocaleString('tr-TR')}
                            {exp ? ` (${exp.text})` : ''}
                          </p>
                        );
                      })()
                    ) : (
                      <p className="font-medium text-gray-900">Süresiz</p>
                    )}
                  </div>
                </div>

                {/* Download Logs */}
                <div className="mt-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    İndirme Geçmişi
                  </h3>

                  {logsLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                      <p className="text-sm text-gray-500 mt-2">Yükleniyor...</p>
                    </div>
                  ) : downloadLogs.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <Download className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">Henüz indirme yapılmamış</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                      {downloadLogs.map((log, index) => {
                        const ua = parseUserAgent(log.user_agent);
                        return (
                          <div key={log.id || index} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="text-xs flex items-center gap-1">
                                    <Globe className="h-3 w-3" />
                                    {log.ip_address}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                                    <Monitor className="h-3 w-3" />
                                    {ua.browser} / {ua.os}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs">
                                    {ua.device}
                                  </Badge>
                                </div>
                              </div>
                              <span className="text-xs text-gray-500 shrink-0 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(log.downloaded_at).toLocaleString('tr-TR')}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminFileShare;
